import React, { useMemo } from 'react';

const supportsDateTimeLocal = () => {
  try {
    if (typeof document === 'undefined') return true;
    const input = document.createElement('input');
    input.setAttribute('type', 'datetime-local');
    return input.type === 'datetime-local';
  } catch (_) {
    return true;
  }
};

const splitLocalDateTime = (value) => {
  if (!value || typeof value !== 'string') return { date: '', time: '' };
  const [date, timeWithSeconds] = value.split('T');
  if (!date) return { date: '', time: '' };
  const time = (timeWithSeconds || '').slice(0, 5); // HH:mm
  return { date, time };
};

const normalizeMinForTime = (selectedDate, minDateTime) => {
  if (!minDateTime) return { minDate: '', minTime: '' };
  const { date: minDate, time: minTime } = splitLocalDateTime(minDateTime);
  if (!selectedDate) return { minDate, minTime: '' };
  return { minDate, minTime: selectedDate === minDate ? (minTime || '') : '' };
};

/**
 * Cross-browser date/time field.
 * - Uses native `datetime-local` when supported.
 * - Falls back to separate `date` + `time` inputs (Safari).
 *
 * Expects/returns value formatted as "YYYY-MM-DDTHH:mm".
 */
const DateTimeInput = ({
  id,
  label,
  value,
  onChange,
  required = false,
  min = '',
  stepSeconds = 60,
  error,
  disabled = false,
  placeholder,
  onClearError
}) => {
  const canUseNative = useMemo(() => supportsDateTimeLocal(), []);

  if (canUseNative) {
    return (
      <>
        <label htmlFor={id}>
          {label}{required ? <span className="required-asterisk">*</span> : null}
        </label>
        <input
          type="datetime-local"
          id={id}
          className={`form-control${error ? ' error' : ''}`}
          value={value || ''}
          onChange={(e) => {
            if (onClearError) onClearError();
            onChange?.(e.target.value);
          }}
          min={min || undefined}
          step={String(stepSeconds)}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          onFocus={(e) => {
            // Chromium supports showPicker(); Safari does not.
            if (typeof e.target.showPicker === 'function') {
              try { e.target.showPicker(); } catch (_) {}
            }
          }}
        />
      </>
    );
  }

  const { date, time } = splitLocalDateTime(value);
  const { minDate, minTime } = normalizeMinForTime(date, min);

  const update = (nextDate, nextTime) => {
    if (onClearError) onClearError();
    if (!nextDate || !nextTime) {
      onChange?.('');
      return;
    }
    onChange?.(`${nextDate}T${nextTime}`);
  };

  return (
    <>
      <label>
        {label}{required ? <span className="required-asterisk">*</span> : null}
      </label>
      <div className="combined-inputs">
        <input
          type="date"
          className={`form-control${error ? ' error' : ''}`}
          value={date}
          min={minDate || undefined}
          onChange={(e) => update(e.target.value, time)}
          disabled={disabled}
          required={required}
        />
        <input
          type="time"
          className={`form-control${error ? ' error' : ''}`}
          value={time}
          min={minTime || undefined}
          step={String(stepSeconds)}
          onChange={(e) => update(date, e.target.value)}
          disabled={disabled}
          required={required}
        />
      </div>
    </>
  );
};

export default DateTimeInput;


